import React, {useContext, useMemo, useState} from "react";
import {Button, Modal, Switch, Table, Tag, Typography} from "antd";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {ENV, operationTypeList, REFRESH_TYPE_FUNCTION} from "../../../shared/utils/enum";
import {Sorter} from "../../../shared/components/Sorter";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import ModalBase from "../../../shared/components/modal/ModalBase";
import ServerRefresh from "../../../shared/components/common/ServerRefresh";
import {AuthContext} from "../../../shared/contexts/authContext";
import {internalApi} from "../../../common/axios";
import {useReloadTime} from "../../serverInfo/useServerInfo";
import {buildUrl} from "../../../shared/utils/net";
import {useEventTable} from "../useEventTable";
import {useEventSegments, useSegmentEvents} from "./useEventSegment";
import SegmentEventEditModal from "./SegmentEventEditModal";

const {Text} = Typography;

// 개인화 관리: 세그먼트 대상 이벤트 편성(event_segment_master)을 세그먼트 그룹별로 묶어 그룹 단위로 On/Off 한다.
const SegmentEvent = () => {
    const {currentUser} = useContext(AuthContext);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [selectedSeqs, setSelectedSeqs] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);

    const {data: eventTable, isLoading, error} = useEventTable();
    const {data: segmentList, isLoading: isSegmentLoading, error: segmentError} = useEventSegments();
    const {data: segmentEvents, isLoading: isEventsLoading, error: eventsError, refetch} = useSegmentEvents();
    const {data: reloadTimeData, isLoading: isReloadTimeData, refetch: reloadTimeDataRefetch} = useReloadTime();

    const now = dayjs().unix();

    const changeOnOff = async (body) => {
        try {
            await internalApi.post('events/segment/onoff', body);
            toast.success('On/Off 상태가 변경되었습니다.');
            refetch();
        } catch {
            toast.error('On/Off 변경 실패');
        }
    };

    const showDeleteConfirm = () => {
        Modal.confirm({
            title: '선택 이벤트 삭제',
            content: `선택한 세그먼트 이벤트 ${selectedSeqs.length}개를 삭제합니다. 진행하시겠습니까?`,
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await internalApi.delete(buildUrl('events/segment', {seq: selectedSeqs.join(',')}));
                    toast.success('삭제되었습니다.');
                    setSelectedSeqs([]);
                    refetch();
                } catch {
                    toast.error('삭제 실패');
                }
            },
        });
    };

    const showSelectedCopyConfirm = () => {
        Modal.confirm({
            title: '선택 이벤트 복사',
            content: `선택한 세그먼트 이벤트 ${selectedSeqs.length}개를 복사합니다. 복사 시 시작일과 종료일에 1일씩 더해지고 Off 상태로 생성됩니다.`,
            okText: '복사',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await internalApi.post('events/segment/bulk_copy', {seqList: selectedSeqs});
                    toast.success('복사가 완료되었습니다.');
                    setSelectedSeqs([]);
                    refetch();
                } catch {
                    toast.error('복사 실패');
                }
            },
        });
    };

    const showCopyConfirm = () => {
        Modal.confirm({
            title: 'Live 데이터 복사',
            content: '세그먼트 그룹과 세그먼트 이벤트를 Live 데이터로 덮어씁니다. 진행하시겠습니까?',
            okText: '확인',
            cancelText: '취소',
            onOk: async () => {
                await internalApi.put('events/segment');
                toast.success('라이브 데이터 복사가 완료되었습니다. 서버 갱신을 진행하세요.');
                refetch();
            },
        });
    };

    // 그룹별로 묶는다. 그룹 스위치는 끝나지 않은 이벤트가 하나라도 On 이면 On.
    const groupedData = useMemo(() => {
        const groups = {};
        (segmentEvents || []).forEach(row => {
            const group = groups[row.segmentIdx] ??= {
                key: `group-${row.segmentIdx}`,
                segmentIdx: row.segmentIdx,
                memo: (segmentList || []).find(segment => segment.seq === row.segmentIdx)?.memo,
                items: [],
            };
            group.items.push(row);
        });
        return Object.values(groups)
            .map(group => ({
                ...group,
                activeItems: group.items.filter(item => item.end >= now),
            }))
            .filter(group => showAll || group.activeItems.length > 0)
            .sort((a, b) => b.segmentIdx - a.segmentIdx);
    }, [segmentEvents, segmentList, showAll, now]);

    const groupColumns = [
        {title: '관리 번호', dataIndex: 'segmentIdx', key: 'segmentIdx', width: 100},
        {
            title: '세그먼트 그룹', key: 'memo',
            render: (_, group) => <Tag color={group.memo !== undefined ? 'purple' : 'red'}>{group.memo ?? '삭제된 그룹'}</Tag>,
        },
        {
            title: 'On/Off', key: 'onOff', width: 100,
            render: (_, group) => (
                <Switch
                    checked={group.activeItems.some(item => item.onOff === 1)}
                    disabled={group.activeItems.length === 0}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={checked => changeOnOff({segmentIdx: group.segmentIdx, onOff: checked ? 1 : 0})}
                />
            ),
        },
        {title: '진행 중/예정 이벤트 수', key: 'count', width: 170, render: (_, group) => `${group.activeItems.length}개`},
    ];

    const eventColumns = [
        {title: '시퀀스', dataIndex: 'seq', key: 'seq', width: 100},
        {title: '이벤트', dataIndex: 'typeIndex', key: 'typeIndex', render: (typeIndex) => `${eventTable?.[typeIndex]?.nameText || ''}(${typeIndex})`},
        {
            title: 'On/Off', dataIndex: 'onOff', key: 'onOff',
            render: (_, record) => (
                <Switch
                    checked={record.onOff === 1}
                    disabled={record.end < now}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={checked => changeOnOff({seq: record.seq, onOff: checked ? 1 : 0})}
                    onClick={(_, e) => e.stopPropagation()}
                />
            ),
        },
        {
            title: '운영 타입', dataIndex: 'operationType', key: 'operationType',
            render: (operationType) => {
                const opType = operationTypeList.find(item => item.value === operationType);
                return opType ? <Tag color={opType.color}>{opType.label}</Tag> : null;
            },
        },
        {title: '계정 레벨', dataIndex: 'openLevel', key: 'openLevel'},
        {title: '최대 레벨', dataIndex: 'maxLevel', key: 'maxLevel'},
        {title: 'Start', dataIndex: 'begin', key: 'begin', render: (begin) => dayjs.unix(begin).format('YYYY-MM-DD HH:mm:ss')},
        {
            title: 'End', dataIndex: 'end', key: 'end',
            render: (end) => dayjs.unix(end).format('YYYY-MM-DD HH:mm:ss'),
            sorter: (a, b) => Sorter.DATE(a.end, b.end),
        },
        {title: 'Memo', dataIndex: 'memo', key: 'memo'},
    ];

    if (isLoading || isSegmentLoading || isEventsLoading || isReloadTimeData) return <IsLoading/>;
    if (error || segmentError || eventsError) return <ErrorPage error={error || segmentError || eventsError}/>;

    return (
        <div>
            <div style={{marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8}}>
                <Switch checked={showAll} onChange={setShowAll} checkedChildren="전체 보기" unCheckedChildren="진행 중/예정만"/>
                <Button disabled={selectedSeqs.length === 0} onClick={showSelectedCopyConfirm}>
                    선택 복사{selectedSeqs.length > 0 ? ` (${selectedSeqs.length})` : ''}
                </Button>
                <Button danger disabled={selectedSeqs.length === 0} onClick={showDeleteConfirm}>
                    선택 삭제{selectedSeqs.length > 0 ? ` (${selectedSeqs.length})` : ''}
                </Button>
                {currentUser.target !== ENV.PRODUCTION && (
                    <Button onClick={showCopyConfirm}>Live 데이터 복사하기</Button>
                )}
                <ServerRefresh refreshTypeFunc={REFRESH_TYPE_FUNCTION.GAME_DATA} reloadTimeRefetch={reloadTimeDataRefetch}/>
                <Text type="secondary" style={{fontSize: '14px'}}>
                    마지막 동기화 (이벤트, 상점정보 등) : {reloadTimeData?.game_data_sync_id ? reloadTimeData.game_data_sync_id + ' (KST)' : '정보 없음'}
                </Text>
            </div>

            <Table
                columns={groupColumns}
                dataSource={groupedData}
                expandable={{
                    expandedRowKeys: expandedKeys,
                    onExpandedRowsChange: keys => setExpandedKeys([...keys]),
                    columnTitle: (
                        <button
                            type="button"
                            aria-label={expandedKeys.length ? '모두 접기' : '모두 펼치기'}
                            title={expandedKeys.length ? '모두 접기' : '모두 펼치기'}
                            className={`ant-table-row-expand-icon ant-table-row-expand-icon-${expandedKeys.length ? 'expanded' : 'collapsed'}`}
                            onClick={() => setExpandedKeys(expandedKeys.length ? [] : groupedData.map(g => g.key))}
                        />
                    ),
                    expandedRowRender: (group) => (
                        <Table
                            columns={eventColumns}
                            dataSource={(showAll ? group.items : group.activeItems).map(item => ({...item, key: item.seq}))}
                            rowSelection={{
                                // 그룹별 하위 테이블이라 선택은 전체 seq 목록에 합쳐서 관리한다.
                                selectedRowKeys: selectedSeqs,
                                onChange: (keys) => {
                                    const groupSeqs = new Set(group.items.map(item => item.seq));
                                    setSelectedSeqs(prev => [...prev.filter(seq => !groupSeqs.has(seq)), ...keys]);
                                },
                            }}
                            pagination={false}
                            size="small"
                            scroll={{x: 'max-content'}}
                            onRow={record => ({
                                // 체크박스 칸 클릭은 선택만 하고 수정 창은 열지 않는다.
                                onClick: (e) => !e.target.closest('.ant-table-selection-column') && setSelectedRecord(record),
                                style: {cursor: 'pointer'},
                            })}
                        />
                    ),
                }}
                pagination={false}
                size="middle"
            />

            {selectedRecord && <ModalBase
                open
                rowData={selectedRecord}
                ModalPage={SegmentEventEditModal}
                handleClose={() => {
                    setSelectedRecord(null);
                    refetch();
                }}
            />}
        </div>
    );
};

export default SegmentEvent;
